import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Zap } from 'lucide-react';
import { uploadAPI } from '../lib/api';
import { useOrg } from '../contexts/OrgContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

import { FileDropZone } from '../components/upload/FileDropZone';
import { ProcessingResults } from '../components/upload/ProcessingResults';

export default function UploadPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { credits, isFree, refreshCredits } = useOrg();
  const devMode = credits?.dev_mode;
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [processingResults, setProcessingResults] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Handle shared files from PWA Share Target API
  useEffect(() => {
    if (searchParams.get('shared') !== 'true') return;
    (async () => {
      try {
        const cache = await caches.open('share-target-files');
        const countResp = await cache.match('/shared-file-count');
        if (!countResp) return;
        const count = parseInt(await countResp.text(), 10);
        const sharedFiles = [];
        for (let i = 0; i < count; i++) {
          const resp = await cache.match(`/shared-file-${i}`);
          if (resp) {
            const blob = await resp.blob();
            const name = resp.headers.get('X-File-Name') || `shared-file-${i}`;
            sharedFiles.push(new File([blob], name, { type: blob.type }));
          }
        }
        if (sharedFiles.length > 0) {
          setFiles(sharedFiles);
          toast.success(`${sharedFiles.length} file(s) received via share`);
        }
        await caches.delete('share-target-files');
      } catch (err) {
        console.warn('Failed to load shared files from cache:', err);
      }
      window.history.replaceState(null, '', '/upload');
    })();
  }, [searchParams]);

  // Handle photo from FAB (quick-action button)
  useEffect(() => {
    if (searchParams.get('photo') !== 'true') return;
    try {
      const raw = sessionStorage.getItem('fab_photo');
      if (!raw) return;
      const { name, type, data } = JSON.parse(raw);
      const arr = data.split(',');
      const bstr = atob(arr[1]);
      const u8arr = new Uint8Array(bstr.length);
      for (let i = 0; i < bstr.length; i++) u8arr[i] = bstr.charCodeAt(i);
      const file = new File([u8arr], name, { type });
      setFiles([file]);
      toast.success('Photo ready to upload');
      sessionStorage.removeItem('fab_photo');
    } catch (err) {
      console.warn('Failed to restore photo from session:', err);
    }
    window.history.replaceState(null, '', '/upload');
  }, [searchParams]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  }, []);

  const handleFiles = useCallback((newFiles) => {
    const validFiles = newFiles.filter(file => {
      const ext = file.name.split('.').pop().toLowerCase();
      return ['pdf', 'csv', 'png', 'jpg', 'jpeg', 'webp'].includes(ext);
    });
    if (validFiles.length !== newFiles.length) toast.error('Some files were skipped. Allowed: PDF, CSV, PNG, JPG, WEBP');
    setFiles(prev => [...prev, ...validFiles]);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) handleFiles(Array.from(e.dataTransfer.files));
  }, [handleFiles]);

  const handleFileSelect = (e) => { if (e.target.files) handleFiles(Array.from(e.target.files)); };
  const removeFile = (index) => { setFiles(prev => prev.filter((_, i) => i !== index)); };

  const pollForStatus = async (fileId) => {
    const maxAttempts = 60;
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(r => setTimeout(r, 2000));
      try {
        const res = await uploadAPI.getUploadStatus(fileId);
        const data = res.data;
        const stepLabel = data.processing_status === 'extracting' ? 'Step 1/2 — Extracting'
          : data.processing_status === 'classifying' ? 'Step 2/2 — Classifying'
          : data.processing_message || 'Processing...';
        setProcessingResults(prev => ({
          ...prev, progressMessage: stepLabel, progressPercent: data.processing_progress || 0,
          currentStep: data.processing_status, transactionCount: data.transaction_count || 0,
        }));
        if (data.processing_status === 'completed') return data;
        if (data.processing_status === 'error') throw new Error(data.processing_message || 'Processing failed');
      } catch (error) {
        if (error.message && error.message !== 'Processing failed') continue;
        throw error;
      }
    }
    throw new Error('Processing timed out');
  };

  const uploadAndProcess = async () => {
    if (files.length === 0) return;
    setUploading(true);
    let totalTransactions = 0, totalDebits = 0, totalCredits = 0;
    const processedFiles = [];

    for (const file of files) {
      try {
        const uploadRes = await uploadAPI.uploadAndProcess(file);
        const fileId = uploadRes.data.file_id;
        toast.info(`Processing ${file.name}... This may take a moment for large files.`);
        setProcessingResults({ progressMessage: 'Extracting transactions from file...', progressPercent: 10, totalTransactions: 0, totalDebits: 0, totalCredits: 0, netAmount: 0, files: [] });
        const result = await pollForStatus(fileId);
        const txnCount = result.transaction_count || 0;
        let debits = 0, credits = 0, duplicates = 0;
        try {
          const summary = await uploadAPI.getUploadSummary(fileId);
          debits = summary.data.total_debits || 0;
          credits = summary.data.total_credits || 0;
          duplicates = summary.data.duplicate_count || 0;
        } catch {}
        totalTransactions += txnCount;
        totalDebits += debits;
        totalCredits += credits;
        processedFiles.push({ id: fileId, filename: file.name, transactions: txnCount, debits, credits, duplicates });
      } catch (error) {
        const msg = error.response?.data?.detail || error.message || 'Upload failed';
        toast.error(`${file.name}: ${msg}`);
        processedFiles.push({ filename: file.name, transactions: 0, debits: 0, credits: 0, error: msg });
      }
    }

    setProcessingResults({
      totalTransactions, totalDebits, totalCredits,
      netAmount: totalCredits - totalDebits, files: processedFiles,
    });
    setUploadedFiles(processedFiles);
    setFiles([]);
    setUploading(false);
    refreshCredits?.();
    if (totalTransactions > 0) toast.success(`${totalTransactions} transactions extracted from ${processedFiles.length} file(s)`);
  };

  const handleDeleteFile = async () => {
    if (!deleteConfirm) return;
    try {
      await uploadAPI.deleteFile(deleteConfirm.id);
      toast.success(`Deleted "${deleteConfirm.filename}"`);
      setProcessingResults(prev => {
        if (!prev) return prev;
        const remaining = prev.files.filter(f => f.id !== deleteConfirm.id);
        const txns = remaining.reduce((s, f) => s + (f.transactions || 0), 0);
        const deb = remaining.reduce((s, f) => s + (f.debits || 0), 0);
        const cred = remaining.reduce((s, f) => s + (f.credits || 0), 0);
        return { ...prev, files: remaining, totalTransactions: txns, totalDebits: deb, totalCredits: cred, netAmount: cred - deb };
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete file');
    } finally {
      setDeleteConfirm(null);
    }
  };

  const totalAvailable = credits?.total_available || 0;

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fade-in">
      {/* Credit info banner */}
      {credits && totalAvailable <= 0 && !credits?.overage_allowed && !devMode && (
        <div className="flex items-center gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200" data-testid="credits-exhausted-banner">
          <Zap className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-800">You're running low on credits</p>
            <p className="text-xs text-amber-600 mt-0.5">You can still upload files, but processing may be limited. Top up or upgrade for more credits.</p>
          </div>
          <Link to="/settings" data-testid="upgrade-from-upload-btn">
            <Button size="sm" className="bg-[#D94E18] hover:bg-[#C2410C] text-white text-xs h-8 rounded-full px-4">Top up</Button>
          </Link>
        </div>
      )}

      <FileDropZone
        files={files} dragActive={dragActive} uploading={uploading}
        isFree={isFree} devMode={devMode}
        onDrag={handleDrag} onDrop={handleDrop}
        onFileSelect={handleFileSelect} onRemoveFile={removeFile}
        onUpload={uploadAndProcess}
      />

      <ProcessingResults
        results={processingResults}
        onUploadMore={() => { setProcessingResults(null); setUploadedFiles([]); }}
        onGoToReview={() => navigate('/transactions')}
        onDeleteFile={setDeleteConfirm}
      />

      {/* Instructions */}
      {!processingResults && (
        <Card>
          <CardContent className="pt-6">
            <h3 className="text-sm font-semibold text-zinc-900 mb-3">How it works</h3>
            <ol className="space-y-2 text-sm text-zinc-600">
              {['Upload your bank statement (PDF, CSV, or photo)', 'AI extracts transactions and suggests categories', 'First look: review the summary and approve transactions', 'Second look: edit and correct approved transactions, then post to GL'].map((text, idx) => (
                <li key={text} className="flex gap-2">
                  <span className="w-5 h-5 rounded-full bg-[#D94E18] text-white text-xs flex items-center justify-center flex-shrink-0">{idx + 1}</span>
                  {text}
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Upload?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete "{deleteConfirm?.filename}" and all associated transactions that haven't been posted to the GL yet. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteFile} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-upload-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
